import { useUpdateTaskMutation } from 'api/collaborateApi';
import { Check, Users } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UpdateTaskParams } from 'types/api/collaborate';
import { TaskAttributes } from 'types/api/tasks';
import { Avatar, Button, DropdownMenu, ToggleGroup, ToggleGroupItem } from 'ui';
import { getSelectedSiteCollaborateInfo } from '../../../../../store/siteInfoSlice';
import { setNotifiedUsers } from '../../../../../store/tasksSlice';
import { getUser, setTaskotifyUsers } from '../../../../../store/userSlice';

interface Props {
    taskDetails: Partial<TaskAttributes> | undefined;
    isPendingTask?: boolean;
}

interface User {
    value: string | number;
    label: string | undefined;
    email: string;
    avatar: string;
    role: string;
    checked: boolean;
    responsible: boolean;
}

export const TeamMembers = ({ taskDetails }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const siteDetails = useSelector(getSelectedSiteCollaborateInfo);
    const userData = useSelector(getUser);
    const [updateTask] = useUpdateTaskMutation()
    // Track selected users internally
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const previousSelectedUsers = useRef<string[]>([]);
    const users: User[] = useMemo(() => {
        if (!siteDetails) return [];
        if (taskDetails) {
            const userList = taskDetails?.notify_wp_feedback_users?.map((member) => ({
                value: member.value,
                label: member.label,
                email: member.email,
                avatar: member.avatar || '',
                role: member.role || '',
                checked: member.checked,
                responsible: member.responsible,
            }));

            const initialSelected = userList?.
                filter(user => user.checked)
                .map(user => String(user.value));
            setSelectedUsers(initialSelected || []);
            previousSelectedUsers.current = initialSelected || [];

            return userList || []
        }

        if (siteDetails) {
            const userList = siteDetails.result.users.map((member) => ({
                value: member.value,
                label: member.label,
                email: member.email,
                avatar: member.avatar || '',
                role: member.role,
                checked: false,
                responsible: false,
            }));
            if (selectedUsers.length === 0) {
                const initialSelected = userList
                    .filter(user => user.checked)
                    .map(user => String(user.value));
                setSelectedUsers(initialSelected);
                previousSelectedUsers.current = initialSelected;
            }
            return userList
        }


        return [];
    }, [siteDetails, taskDetails]);

    useEffect(() => {
        dispatch(setNotifiedUsers(selectedUsers))
    }, [dispatch, selectedUsers])

    const handleUserSelection = useCallback((newSelectedUsers: string[]) => {
        // Find the difference between previous and new selections
        const added = newSelectedUsers.filter(id => !previousSelectedUsers.current.includes(id));
        const removed = previousSelectedUsers.current.filter(id => !newSelectedUsers.includes(id));

        // Determine which user was toggled
        const toggledUserId = added.length > 0 ? added[0] : removed[0];
        const wasAdded = added.length > 0;

        // Find the toggled user data
        const toggledUser = users.find(user => String(user.value) === toggledUserId);

        if (toggledUser && taskDetails) {
            // Create API payload
            const payload = {
                method: "task_notify_users",
                task_id: taskDetails.task_id,
                user_id: userData && String(userData.id) || "",
                value: {
                    id: String(toggledUser.value),
                    checked: wasAdded, // true if added, false if removed
                    label: toggledUser.label,
                    responsible_user: toggledUser.responsible
                },
                guest_user_id: "",
                guest_author_name: toggledUser.label || ""
            };
            updateTask(payload as UpdateTaskParams)
        }

        // Update state for next comparison
        setSelectedUsers(newSelectedUsers);
        if (!taskDetails) {
            dispatch(setTaskotifyUsers(newSelectedUsers))
        }
        previousSelectedUsers.current = newSelectedUsers;
    }, [users, taskDetails, userData, updateTask, dispatch]);

    return (
        <DropdownMenu modal={false} >
            <DropdownMenu.Trigger asChild>
                <Button
                    tooltip={t('team_members.tooltip.notified_users', 'People who’ll get updates on this task')}
                    toolTipPortal
                    variant="ghost"
                    size="icon"
                >
                    <Users size={18} aria-hidden="true" />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                portal={true}
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                align="start"
                className="flex flex-col gap-2 p-1 rounded-xl max-h-96 w-72 overflow-y-auto snap-y snap-mandatory pointer-events-auto">
                <ToggleGroup
                    className="flex flex-col gap-2 items-center"
                    type="multiple"
                    value={selectedUsers}
                    onValueChange={handleUserSelection}
                >
                    {users && users.length > 0 &&
                        users.map((member) => (
                            <ToggleGroupItem
                                key={member.value}
                                size="sm"
                                variant="tertiary"
                                value={member.value?.toString() || ''}
                                className="p-1 w-full data-[state=on]:outline data-[state=on]:outline-1 data-[state=on]:outline-tertiary flex gap-2 rounded-lg h-full cursor-pointer items-start rounded-[10px] border-none items-center  transition-shadow duration-300 data-[state=on]:hover:shadow-md data-[state=on]:bg-tertiary/5 data-[state=on]:text-[#272D3] snap-end"
                            >
                                <Avatar
                                    title={member?.label}
                                    id={member?.label}
                                    email={member?.email}
                                />
                                <span className="flex flex-row justify-between w-full items-center overflow-hidden">
                                    <span className="text-textcolor flex flex-col gap-0.5 items-start">
                                        <span className="font-medium leading-none block max-w-[120px] truncate text-sm">
                                            {member?.label}
                                        </span>
                                        <span className="capitalize leading-none text-gray-400 text-xs max-w-[120px] truncate">
                                            {t(`team_members.roles.${member?.role?.toLowerCase()}`, member?.role)}
                                        </span>
                                    </span>
                                    <span className='flex flex-row gap-1'>
                                        <AnimatePresence mode="wait">
                                            {selectedUsers.includes(String(member?.value)) ? (
                                                <motion.span
                                                    key="notified"
                                                    initial={{ y: -20, opacity: 0 }}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    exit={{ y: -20, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="flex flex-row gap-1 items-center text-tertiary text-xs"
                                                >
                                                    {t('team_members.notification.will_be_notified', 'Will be notified')}
                                                    <Check size={16} aria-hidden="true" />
                                                </motion.span>
                                            ) : (
                                                <motion.span
                                                    key="not-notified"
                                                    initial={{ y: -20, opacity: 0 }}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    exit={{ y: -20, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="flex flex-row gap-1 items-center text-gray-500 text-xs mr-1.5"
                                                >
                                                    {t('team_members.notification.wont_be_notified', "Won't be notified")}
                                                </motion.span>
                                            )}
                                        </AnimatePresence>
                                    </span>

                                </span>
                            </ToggleGroupItem>
                        ))}
                </ToggleGroup>
            </DropdownMenu.Content>
        </DropdownMenu>
    );
};